import { Box, Button, FormikTextInput, Modal, toast, Toggle } from '@palmetto/palmetto-components';
import { Field, Form, Formik, FormikHelpers } from 'formik';
import { useMemo, ChangeEvent } from 'react';
import * as yup from 'yup';

import { PausedSettings } from 'types';

export interface PausedSettingsFormValues {
  pausedDays: number;
  inherit: boolean;
}

const pausedSettingsSchema = yup.object({
  pausedDays: yup
    .number()
    .integer('Paused Days must be a whole number')
    .min(0, 'Paused Days must be at least 0')
    .max(99, 'Paused Days cannot exceed 99 days')
    .when('inherit', {
      is: false,
      then: (schema) => schema.required('Paused Days is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  inherit: yup.boolean().required(),
});

interface PausedSettingsEditModalProps {
  isOpen: boolean;
  handleClose: () => void;
  handleSubmit: (values: PausedSettingsFormValues) => Promise<void>;
  currentSettings?: PausedSettings;
  modalId: string;
  modalTitle: string;
  submitButtonText?: string;
}

export const PausedSettingsEditModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  currentSettings,
  modalId,
  modalTitle,
  submitButtonText = 'Save Changes',
}: PausedSettingsEditModalProps) => {
  const initialValues = useMemo(() => {
    return {
      pausedDays: currentSettings?.pausedDays || 0,
      inherit: currentSettings?.inherit ?? false,
    };
  }, [currentSettings]);

  const showErrorToast = (message: string) => {
    toast.error(message, {
      style: {
        maxWidth: '500px',
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
      },
    });
  };

  const onSubmit = async (
    values: PausedSettingsFormValues,
    { setSubmitting, resetForm }: FormikHelpers<PausedSettingsFormValues>,
  ) => {
    try {
      await handleSubmit({ ...values });
      resetForm();
      handleClose();
      toast.success('Paused days settings updated successfully');
    } catch (error: any) {
      showErrorToast(`Failed to update paused days settings: ${error?.data?.message || String(error)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={pausedSettingsSchema}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnBlur
    >
      {({ isSubmitting, resetForm, submitForm, values, setFieldValue }) => (
        <Form>
          <Modal
            isOpen={isOpen}
            maxWidth="4xl"
            ariaLabelledBy={modalId}
            onDismiss={() => {
              if (!isSubmitting) {
                resetForm();
                handleClose();
              }
            }}
          >
            <Modal.Header
              id={modalId}
              title={modalTitle}
              onDismiss={() => {
                if (!isSubmitting) {
                  resetForm();
                  handleClose();
                }
              }}
            />
            <Modal.Body background="secondary" childGap="lg">
              <Box childGap={{ base: 'lg', desktop: 'xl' }}>
                <Box borderWidth="xs" borderColor="grey-100" padding="md" radius="md" childGap="md">
                  <Box direction="row" justifyContent="space-between" alignItems="center" gap="md">
                    <Box>Paused Days</Box>
                    <Box width="xl" textAlign={{ base: 'left', desktop: 'right' }}>
                      <Field
                        type="number"
                        isDisabled={values.inherit || isSubmitting}
                        name="pausedDays"
                        id="pausedDays"
                        component={FormikTextInput}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Toggle
                id="inherit"
                label="Inheriting from parent"
                isChecked={values.inherit}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setFieldValue('inherit', e.target.checked);
                }}
                isDisabled={isSubmitting}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                tone="neutral"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                onClick={() => {
                  resetForm();
                  handleClose();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                type="submit"
                onClick={submitForm}
              >
                {submitButtonText}
              </Button>
            </Modal.Footer>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};
